import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { NgFor, NgIf } from '@angular/common';
import { ClrCommonFormsModule, ClrIconModule, ClrModalModule, ClrTooltipModule } from '@clr/angular';
import {ClarityIcons, floppyIcon, trashIcon, infoCircleIcon, pdfFileIcon} from '@cds/core/icon';
import { AlertService, AuthService, FileStoreService, IAlertType, MaterialStoreService } from '../../../../_shared/services';
import { UploadComponent } from '../../../../core/upload/upload.component';
import {
  Files,
  MaterialModel,
  MaterialReadSingleResponse,
  MaterialUpdatePayload
} from '../../../../_shared/interfaces';
import { filesBaseUrl } from "../../../../_shared/utils/rest-url";
import { NgxExtendedPdfViewerModule } from "ngx-extended-pdf-viewer";

@Component({
  selector: 'app-edit-material',
  standalone: true,
  imports: [
    ClrCommonFormsModule,
    ReactiveFormsModule,
    NgIf,
    NgFor,
    ClrIconModule,
    ClrModalModule,
    ClrTooltipModule,
    UploadComponent,
    NgxExtendedPdfViewerModule
  ],
  templateUrl: './edit-material.component.html',
  styleUrl: './edit-material.component.sass'
})
export class EditMaterialComponent implements OnInit {
  public editMaterialForm: FormGroup = new FormGroup({});
  material: MaterialModel | null = null;
  datenblaetter: Files[] = [];
  modalContent: string = '';
  modalUrl: string = '';
  modalSize: string = 'lg';
  showPDF: boolean = false;

  @Input() editMaterialId: number = 0;
  @Output() updatedMaterial = new EventEmitter<boolean>();

  constructor(
    private materialStore: MaterialStoreService,
    private fb: FormBuilder,
    private alertDispatcher: AlertService,
    private authService: AuthService,
    private fileStore: FileStoreService
  ) {
    ClarityIcons.addIcons(floppyIcon, pdfFileIcon, trashIcon, infoCircleIcon);
  }

  ngOnInit(): void {
    this.initForm();
    this.loadMaterial(this.editMaterialId);
  }

  initForm() {
    this.editMaterialForm = this.fb.group({
      material_id: [this.editMaterialId],
      material: [''],
      werkstoffbezeichnung: [''],
      werkstoffnummer: [''],
      waermebehandlungszustand: [''],
      description: [''],
      alternative: [0],
      datenblatt: [''],
    });
  }

  loadMaterial(materialId: number) {
    if (!materialId) {
      return;
    }

    this.materialStore.getMaterial(materialId).subscribe({
      next: (data: MaterialReadSingleResponse) => {
        if (!data || !data.data) {
          this.alertDispatcher.show({
            message: ' Material konnte nicht geladen werden.',
            clrAlertType: IAlertType.WARNING
          }, 5000);
          return;
        }
        this.material = data.data;
        this.editMaterialForm.patchValue({
          material_id: data.data.material_id,
          material: data.data.material,
          werkstoffbezeichnung: data.data.werkstoffbezeichnung,
          werkstoffnummer: data.data.werkstoffnummer,
          waermebehandlungszustand: data.data.waermebehandlungszustand,
          description: data.data.description ?? '',
          alternative: data.data.alternative ?? 0,
          datenblatt: data.data.datenblatt ?? '',
        });
        this.refreshDatenblaetter();
      },
      error: () => {}
    });
  }

  getDatenblattIds(): string[] {
    const raw = String(this.editMaterialForm.get('datenblatt')?.value ?? '');
    return raw
      .split(/[\s,;]+/)
      .map((item) => item.trim())
      .filter((item) => item.length > 0);
  }

  getDownloadUrl(fileId: string): string {
    const token = this.authService.getAccessToken();
    const base = `${filesBaseUrl()}/download_file`;
    return token
      ? `${base}?file_id=${encodeURIComponent(fileId)}&token=${encodeURIComponent(token)}`
      : `${base}?file_id=${encodeURIComponent(fileId)}`;
  }

  openPdf(file: Files) {
    if (!file?.id) {
      return;
    }
    this.modalUrl = this.getDownloadUrl(file.id);
    this.modalContent = file.fileName || file.id;
    this.modalSize = 'lg';
    this.showPDF = true;
  }

  removeDatenblatt(file: Files) {
    if (!file?.id) {
      return;
    }
    if (!confirm(`Datei "${file.fileName || file.id}" wirklich löschen?`)) {
      return;
    }
    this.fileStore.deleteFile(file.id).subscribe({
      next: () => {
        const current = this.getDatenblattIds();
        const next = current.filter((id) => id !== file.id);
        this.editMaterialForm.patchValue({ datenblatt: next.join(',') });
        this.updateMaterial(false);
        this.refreshDatenblaetter();
      },
      error: () => {
        this.alertDispatcher.show({
          message: ' Datei konnte nicht gelöscht werden. ',
          clrAlertType: IAlertType.WARNING
        }, 5000);
      }
    });
  }

  handleUploadSuccess(event: { id: string; fileName?: string }) {
    const current = this.getDatenblattIds();
    if (!current.includes(event.id)) {
      current.push(event.id);
      this.editMaterialForm.patchValue({ datenblatt: current.join(',') });
      this.updateMaterial(false);
      this.refreshDatenblaetter();
    }
  }

  refreshDatenblaetter() {
    const ids = this.getDatenblattIds();
    if (ids.length === 0) {
      this.datenblaetter = [];
      return;
    }

    this.fileStore.getFilesByIds(ids).subscribe({
      next: (res) => {
        this.datenblaetter = res?.object ?? [];
      },
      error: () => {
        this.datenblaetter = [];
      }
    });
  }

  updateMaterial(emitUpdate: boolean = true) {
    const raw = this.editMaterialForm.value;
    const payload: MaterialUpdatePayload = {
      material_id: Number(raw?.material_id ?? 0),
      material: raw?.material ?? '',
      werkstoffbezeichnung: raw?.werkstoffbezeichnung ?? '',
      werkstoffnummer: raw?.werkstoffnummer ?? '',
      waermebehandlungszustand: raw?.waermebehandlungszustand ?? '',
      description: raw?.description ?? '',
      alternative: raw?.alternative === '' || raw?.alternative === null
        ? null
        : Number(raw?.alternative ?? 0),
      datenblatt: raw?.datenblatt ?? '',
    };

    this.materialStore.updateMaterial(payload).subscribe({
      next: (data) => {
        if (!data || data.status?.toLowerCase?.() === 'error') {
          this.alertDispatcher.show({
            message: ' Material konnte nicht geändert werden.',
            clrAlertType: IAlertType.WARNING
          }, 5000);
          return;
        }
        if (emitUpdate) {
          this.updatedMaterial.emit(true);
        }
      },
      error: () => {}
    });
  }

  cancelEdit() {
    this.updatedMaterial.emit(true);
  }

}
